import { useTranslation } from 'react-i18next';
import { AlertTriangle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Genau die Form, in der `products.validation_warnings` in der Karte ankommt. */
export interface ValidationWarning {
  field?: string;
  message: string;
  severity?: string;
  rule_id?: string;
}

interface ValidationNoticeProps {
  /** `null` heisst "nie geprueft", `[]` heisst "geprueft, nichts gefunden". */
  warnings: ValidationWarning[] | null | undefined;
}

/**
 * Dringlichkeitsrang. Kleiner ist dringender.
 *
 * `error` steht in den LIVE-Daten heute nicht (gemessen 2026-08-02: 69.006
 * Warnungen, ausnahmslos `warning`) — der erzeugende Code kann es aber sehr
 * wohl (`_shared/product-validation.ts`, sieben Stellen). Die Rangfolge kostet
 * nichts und traegt, sobald die Regel erstmals feuert; sie hier wegzulassen
 * hiesse, auf den Tag zu warten, an dem es auffaellt.
 */
const SEVERITY_RANK: Record<string, number> = { error: 0, warning: 1 };

function rankOf(severity: string | undefined): number {
  return SEVERITY_RANK[severity ?? ''] ?? 2;
}

/**
 * Die dringendste Warnung einer Liste, nach demselben Rang wie die
 * angezeigte Reihenfolge unten. `null` bei leerer/fehlender Liste.
 *
 * Exportiert, weil die Leiste (`WorkbenchRail`) dieselbe Dringlichkeit
 * braucht (die Zeile unter dem Titel zeigt nur die EINE wichtigste Regel,
 * keine Liste). Zwei eigene Kopien der Rangfolge wuerden auseinanderlaufen —
 * dieselbe Begruendung wie bei `listenSchluessel` in `CollapsibleField`.
 */
export function mostUrgentWarning(
  warnings: ValidationWarning[] | null | undefined,
): ValidationWarning | null {
  if (!warnings || warnings.length === 0) return null;
  return [...warnings].sort((a, b) => rankOf(a.severity) - rankOf(b.severity))[0];
}

/**
 * Uebersetzt eine einzelne Warnung ueber ihre `rule_id`, mit Rueckfall auf
 * die rohe (englische) `message`.
 *
 * Exportiert und rein, damit `WorkbenchRail` dieselbe Uebersetzungsregel
 * benutzt statt sie ein zweites Mal zu schreiben — genau die Falle, gegen
 * die `listenSchluessel` in `CollapsibleField` schon einmal steht.
 * `i18nExists` ist ein eigener Parameter statt eines `i18n`-Objekts, damit
 * die Funktion ohne einen echten i18next-Kontext testbar bleibt.
 */
export function warningLabel(
  warning: ValidationWarning,
  t: (key: string) => string,
  i18nExists: (key: string) => boolean,
): string {
  const ruleKey = warning.rule_id ? `validation.rules.${warning.rule_id}` : null;
  return ruleKey && i18nExists(`workbench:${ruleKey}`) ? t(ruleKey) : warning.message;
}

/**
 * Die Hinweiszeile ueber dem Diff.
 *
 * **Warum uebersetzt und nicht durchgereicht:** die `message` in der Datenbank
 * ist hartkodiertes Englisch aus `_shared/product-validation.ts`, und der
 * Writer speichert sie unveraendert. Roh angezeigt stuende ein englischer Satz
 * mitten in einer deutschen Oberflaeche — auf dem meistgenutzten Pfad der
 * Werkbank. Uebersetzt wird deshalb ueber die `rule_id`, die stabil und
 * sprachneutral ist.
 *
 * **Warum trotzdem ein Rueckfall auf die Rohmeldung:** eine neue Regel im
 * Backend darf hier nicht zu einer leeren Zeile fuehren. Englisch ist
 * schlechter als Deutsch und weit besser als nichts — und "nichts" waere hier
 * besonders teuer, weil die Abwesenheit eines Hinweises selbst eine Aussage
 * ist ("durchwinken").
 *
 * Genau deshalb rendert die Komponente bei leerer Liste `null`: eine leere
 * Zeile waere Rauschen, und ein "keine Beanstandungen"-Kasten auf jeder Karte
 * ist nach dem dritten Produkt unsichtbar.
 */
export function ValidationNotice({ warnings }: ValidationNoticeProps) {
  const { t, i18n } = useTranslation('workbench');

  if (!warnings || warnings.length === 0) return null;

  // Stabil sortieren: `Array.prototype.sort` ist in modernen Engines stabil,
  // die Reihenfolge innerhalb derselben Dringlichkeit bleibt also die des
  // Backends. Ohne diese Zusicherung sprаenge die Zeile bei jedem Render.
  const ordered = [...warnings].sort((a, b) => rankOf(a.severity) - rankOf(b.severity));

  return (
    <div className="border-b border-border bg-warning-light/40 px-4 py-2">
      <ul className="flex flex-col gap-1">
        {ordered.map((warning, index) => {
          const isError = warning.severity === 'error';
          const Icon = isError ? XCircle : AlertTriangle;

          // Uebersetzung ueber die Regel; nur wenn es sie wirklich gibt.
          // `i18n.exists` statt eines Vergleichs "Rueckgabe === Schluessel":
          // letzteres waere bei einer Uebersetzung, die zufaellig wie ihr
          // Schluessel aussieht, falsch. Ausgelagert in `warningLabel`, damit
          // die Leiste dieselbe Regel benutzt statt sie zu duplizieren.
          const text = warningLabel(warning, t, (key) => i18n.exists(key));

          const fieldKey = warning.field ? `validation.fields.${warning.field}` : null;
          const fieldLabel =
            fieldKey && i18n.exists(`workbench:${fieldKey}`) ? t(fieldKey) : warning.field;

          return (
            <li
              key={`${warning.rule_id ?? 'ohne-regel'}-${index}`}
              className="flex items-start gap-2 text-xs"
            >
              <Icon
                className={cn(
                  'mt-0.5 h-3.5 w-3.5 flex-shrink-0',
                  isError ? 'text-destructive' : 'text-warning-ink',
                )}
                aria-hidden="true"
              />
              <span className="min-w-0">
                {fieldLabel && (
                  <span className="mr-1 font-medium text-foreground/70">{fieldLabel}</span>
                )}
                <span>{text}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
